import { ClientInfoStatus, PAST_CLIENT_STATUSES } from '@common/interfaces/client'
import { ThreadsFilter } from '@common/interfaces/threadsFilter'
import {
  DEFAULT_ARC_SETTINGS,
  NotificationSettingType,
  toAimStatus,
  UserRole,
  USER_ROLE_LABEL,
  userRoleGroups,
} from '@common/interfaces/user'
import { createSelector } from 'reselect'
import { getNotificationDeviceType } from '../../helpers/platform'
import { IRootState } from '../reducer'
import { prefix } from './actions'

const { ADMIN, PORTFOLIO_ADMIN, CLIENT_USER, PORTFOLIO_USER, UW_USER, BDO, PARTICIPANT_USER } =
  UserRole

const profile = (state: IRootState) => state[prefix]

export const isInitialized = createSelector(profile, ({ isInitialized }) => isInitialized)

export const isLoading = (state: IRootState) => state[prefix].loadingArray.length > 0

export const error = createSelector(profile, ({ error }) => error)

export const isChangedPwd = createSelector(profile, ({ isChangedPwd }) => isChangedPwd)

export const isRecoveryRequested = createSelector(
  profile,
  ({ isRecoveryRequested }) => isRecoveryRequested,
)

export const isConfirmed = createSelector(profile, ({ isConfirmed }) => isConfirmed)

export const isPasswordSetup = createSelector(profile, ({ isPasswordSetup }) => isPasswordSetup)

export const invitedUser = createSelector(profile, ({ invitedUser }) => invitedUser)

export const user = createSelector(profile, ({ user }) => user)

export const userId = createSelector(user, (user) => user?.id)

export const role = createSelector(user, (user) => user?.role)

export const clientMemberships = createSelector(user, (user) => user?.clientMemberships ?? [])

export const isAdmin = createSelector(user, ({ role }) => role === ADMIN)

export const isBDO = createSelector(user, ({ role }) => role === BDO)

export const isUW = createSelector(user, ({ role }) => role === UW_USER)

export const isParticipant = createSelector(user, ({ role }) => role === PARTICIPANT_USER)

export const isProspectUser = createSelector(
  profile,
  ({ user }) => user?.role === CLIENT_USER && user?.isProspectUser,
)

export const isClientUser = createSelector(user, ({ role }) => role === CLIENT_USER)

// Clients and participants, including an admin in Client View: none of them can sign in to Dwight's Box.
export const isExternalUser = createSelector(user, ({ role }) =>
  [CLIENT_USER, PARTICIPANT_USER].includes(role),
)

export const isAdminLoggedAsClient = createSelector(
  profile,
  ({ user }) => user?.role === CLIENT_USER && user?.isAdminLoggedAsClient,
)

export const isExtendedRightsRole = createSelector(user, ({ role }) =>
  [ADMIN, PORTFOLIO_ADMIN, PORTFOLIO_USER, UW_USER].includes(role),
)

export const isPortfolioUser = createSelector(
  role,
  (role) => !!role && userRoleGroups.PORTFOLIO.includes(role),
)

export const isAdminRightsRole = createSelector(
  user,
  ({ role, isAdminLoggedAsClient }) =>
    [ADMIN, PORTFOLIO_ADMIN, UW_USER].includes(role) && !isAdminLoggedAsClient,
)

export const isAuthenticated = createSelector(user, (user) => !!user)

/**
 * The viewer's own name as it should be shown to them, nickname first — matching
 * what teammates see for them everywhere else (see the server's `displayName`).
 */
export const fullName = createSelector(profile, ({ user }) =>
  user ? user.nickname?.trim() || `${user.firstName} ${user.lastName}` : null,
)

/**
 * Whether this user lets the calendar sweep derive a status for them. Absent
 * reads as true — the column defaults on, and a profile still loading should not
 * render the switch as off and then flip it.
 */
export const deriveStatusFromCalendar = createSelector(
  user,
  (user) => user?.deriveStatusFromCalendar ?? true,
)

/**
 * The viewer's own status as stored, WITHOUT expiry applied.
 *
 * Raw because this is memoized on the user object: resolving expiry here would
 * be computed once and then never revisited, so an elapsed status would sit on
 * screen for as long as the tab stays open. Consumers pair it with
 * `liveAimStatus` and `useAimStatusTick`.
 */
export const ownAimStatus = createSelector(user, (user) => (user ? toAimStatus(user) : null))

export const avatar = createSelector(profile, ({ user }) => user?.avatar)

export const viewRowNumbers = createSelector(
  user,
  (user) => user?.displaySettings?.viewRowNumbers !== false,
)

/** Absent means unmuted — the sound is on unless someone has turned it off. */
export const muteMessageSound = createSelector(
  user,
  (user) => user?.displaySettings?.muteMessageSound === true,
)

/** The saved Threads filter. Someone who had the old Unread switch on opens on Unread. */
export const aimThreadsFilter = createSelector(user, (user): ThreadsFilter => {
  const settings = user?.displaySettings
  if (settings?.aimThreadsFilter) return settings.aimThreadsFilter
  return settings?.aimUnreadOnly ? ThreadsFilter.Unread : ThreadsFilter.Active
})

/** Whether AIM is ticked for banners in this device's Desktop/Mobile column. */
export const isMessageBannerEnabled = createSelector(
  user,
  (user) =>
    user?.notificationSettings?.[getNotificationDeviceType()]?.includes(
      NotificationSettingType.Messages,
    ) === true,
)

export const aiDossier = createSelector(user, (user) => user?.aiDossier ?? '')

export const aiLearnedProfile = createSelector(user, (user) => user?.aiLearnedProfile ?? '')

export const aiLearnedProfileUpdatedAt = createSelector(
  user,
  (user) => user?.aiLearnedProfileUpdatedAt ?? null,
)

export const aiSchedulePreference = createSelector(user, (user) => user?.aiSchedulePreference ?? '')

export const draftEmailResponses = createSelector(
  user,
  (user) => user?.arcSettings?.draftEmailResponses ?? DEFAULT_ARC_SETTINGS.draftEmailResponses,
)

export const defaultModel = createSelector(
  user,
  (user) => user?.arcSettings?.defaultModel ?? DEFAULT_ARC_SETTINGS.defaultModel,
)

export const arcVoiceName = createSelector(
  user,
  (user) => user?.arcSettings?.voiceName ?? DEFAULT_ARC_SETTINGS.voiceName,
)

export const arcVoiceAccent = createSelector(
  user,
  (user) => user?.arcSettings?.voiceAccent ?? DEFAULT_ARC_SETTINGS.voiceAccent,
)

export const arcVoiceCutIn = createSelector(
  user,
  (user) => user?.arcSettings?.voiceCutIn ?? DEFAULT_ARC_SETTINGS.voiceCutIn,
)
export const hasFirefliesApiKey = createSelector(user, (user) => !!user?.hasFirefliesApiKey)

export const aiVoiceTone = createSelector(user, (user) => user?.aiVoiceTone ?? '')

export const aiVoiceToneGeneratedAt = createSelector(
  user,
  (user) => user?.aiVoiceToneGeneratedAt ?? null,
)

export const aiVoiceToneRegenerating = createSelector(
  profile,
  ({ voiceToneRegenerating }) => voiceToneRegenerating,
)

export const userRoleLabel = createSelector(role, (role) => USER_ROLE_LABEL[role])

export const shouldShowRoleLabel = createSelector(role, (role) => role !== CLIENT_USER)

export const clientInfo = (state: IRootState) => state[prefix].clientInfo

export const clientName = createSelector(clientInfo, (client) => client?.clientName)

export const isSidebarOpen = createSelector(profile, ({ isSidebarOpen }) => isSidebarOpen)

// ClientInfoStatus.Archived is 'Prospect / Archived' — an archived *prospect*,
// not a former client. It used to land here, which sent archived prospects to
// the past-client menu; they belong in the prospect portal so they can see the
// inactive screen and reactivate.
export const isPastClient = createSelector(
  clientInfo,
  (client) => client && PAST_CLIENT_STATUSES.includes(client?.clientStatus),
)

export const isDueDiligenceClient = createSelector(
  clientInfo,
  (client) => client && client.clientStatus === ClientInfoStatus.DueDiligence,
)

// Pre-underwriting prospect: filling in (or archived out of) the intake, not yet
// in diligence. Drives the prospect portal sidebar and the inactive screen.
export const isIntakeProspectClient = createSelector(
  clientInfo,
  (client) =>
    client && [ClientInfoStatus.Prospect, ClientInfoStatus.Archived].includes(client?.clientStatus),
)

export const requiresTwoFactor = createSelector(
  profile,
  ({ requiresTwoFactor }) => requiresTwoFactor,
)

export const tempUserId = createSelector(profile, ({ tempUserId }) => tempUserId)
